import { useStrategy } from '../../hooks/useStrategy';
import { useValueInputComponents } from '../../hooks/useValueInputComponents';
import FilterOperatorErrorRecord from 'customer-data-filters-ui/filterQueryFormat/FilterOperatorErrorRecord';
import { useCallback, useContext } from 'react';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import classNames from 'classnames';
import getIn from 'transmute/getIn';
import { useValidateCondition } from '../../hooks/useValidateCondition';
import styled from 'foundations-theming/utils';

// this styling fixes issues
// https://git.hubteam.com/hubspot/crm-filters/issues/523
// https://git.hubteam.com/hubspot/crm-filters/issues/528
// ideally styling should be fixed in UIComponents
// TODO: try to get rid of this
import { jsx as _jsx } from "react/jsx-runtime";
const FixedStyleInput = styled.div.withConfig({
  displayName: "EditorConditionValueInput__FixedStyleInput",
  componentId: "sc-1etsmim-0"
})(["&&&{.uiDropdown__buttonContents{min-height:22px;}.uiDropdown__buttonLabel{margin-top:-1px;margin-bottom:-3px;}}&&&.md{.uiDropdown__button{padding-top:8px;padding-bottom:8px;}.UIButtonGroup > .uiButton{padding:12px 20px;}}&&&.sm{.Select-control{min-height:32px;font-size:14px;border:none;}.Select{border:1px solid ", ";}.Select-multi-value-wrapper{min-height:32px;> span{margin-top:4px;&:last-of-type{margin-bottom:4px;}}}.Select-input{input{padding-top:5px;padding-bottom:5px;}}.Select-placeholder{padding-top:5px;padding-bottom:5px;}.uiNumberInput{padding-top:5px;padding-bottom:5px;font-size:14px;}.uiDropdown__button{padding-top:4px;padding-bottom:4px;font-size:14px;}.form-control{padding-top:4px;padding-bottom:4px;font-size:14px;}[class^='UIInputGroup__']{.uiNumberInput{height:32px;}.uiDropdown__button{height:32px;}.form-control{height:32px;}}}"], ({
  theme
}) => theme.color['border-core-default']);
const getFirstConditionError = condition => {
  var _condition$field;
  let firstError;
  if ((_condition$field = condition.field) !== null && _condition$field !== void 0 && _condition$field.refinement) {
    firstError = condition.errors[0];
  } else {
    firstError = condition.errors.filter(error => error.get('opts') && !error.get('opts').isRefinement)[0];
  }
  return firstError || FilterOperatorErrorRecord();
};
/**
 * Wrapper for value input component.
 *
 * Provides all necessary data.
 */
const EditorConditionValueInput = ({
  filterFamily,
  condition,
  onChange,
  disabled,
  inputUse,
  size = 'sm',
  hideExtraInputs,
  multiCollapseLimit,
  readOnly
}) => {
  const {
    currencyCode,
    data,
    getIsUngated,
    props: {
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getReferencedObjectType,
    getValueResolver,
    getSpecialOptionsForReferenceType,
    getDefaultTimezone,
    getTimezoneOptions,
    isFiscalYearEnabled,
    isRollingDateOffsetInputEnabled
  } = useStrategy();
  const {
    validateCondition
  } = useValidateCondition();
  const onValueChange = useCallback((evt, options) => {
    let newValue = evt.target.value;
    // HACK to handle https://git.hubteam.com/HubSpot/crm-filters/issues/675
    // in serializer we always set `includeObjectsWithNoValueSet: true` when
    // defaultValue is not `null`. Do the same on value change to display
    // "null value checkbox" right after the conversion happens in the background
    // so users can disable it
    const defaultValue = getIn(['defaultNullValue', 'defaultValue'], newValue);
    if (defaultValue !== null && defaultValue !== undefined) {
      newValue = newValue.setIn(['defaultNullValue', 'includeObjectsWithNoValueSet'], true);
    }
    if (options && options.hasClickedUpdate) {
      onChange(newValue.set('hasTimezones', false));
    } else {
      onChange(newValue);
    }
  }, [onChange]);
  const onBlur = useCallback(() => {
    const newCondition = validateCondition(filterFamily, condition);
    if (newCondition.errors.length) {
      onChange(newCondition);
    }
  }, [onChange, filterFamily, condition, validateCondition]);
  const resolver = getValueResolver(filterFamily, condition);
  const {
    InputComponent,
    InputComponentWrapper
  } = useValueInputComponents(filterFamily, condition);
  if (!InputComponent || !InputComponentWrapper) {
    return null;
  }
  return /*#__PURE__*/_jsx(FixedStyleInput, {
    className: classNames(size || 'sm'),
    children: /*#__PURE__*/_jsx(InputComponentWrapper, {
      InputComponent: InputComponent,
      currencyCode: currencyCode
      // @ts-expect-error we need to merge and expand the `Data and `StateData` types
      ,
      data: data,
      "data-selenium-test": `fr-operator-${condition.constructor.toString()}-input`,
      "data-test-id": `fr-operator-${condition.constructor.toString()}-input`,
      disabled: disabled,
      error: getFirstConditionError(condition),
      filterFamily: filterFamily,
      inputUse: inputUse,
      getReferencedObjectType: getReferencedObjectType,
      getSpecialOptionsForReferenceType: getSpecialOptionsForReferenceType,
      isFiscalYearEnabled: isFiscalYearEnabled,
      isRollingDateOffsetInputEnabled: isRollingDateOffsetInputEnabled,
      multiCollapseLimit: multiCollapseLimit,
      onBlur: onBlur,
      onChange: onValueChange,
      placeholder: condition.field ? condition.field.placeholder : '',
      readOnly: readOnly,
      resolver: resolver,
      getDefaultTimezone: getDefaultTimezone,
      getTimezoneOptions: getTimezoneOptions,
      getIsUngated: getIsUngated,
      condition: condition,
      hideExtraInputs: hideExtraInputs,
      useOnePanel: useOnePanel
    })
  });
};
export default EditorConditionValueInput;